// src/features/askPortfolio/AskPopout.jsx
//
// ASK-PORTFOLIO-S1: Self-contained Ask Portfolio popout panel.
//
// Props:
//   scope:          { type: 'location'|'lease'|'portfolio', id: string|null, label: string } | null
//   onScopeChange:  (scope | null) => void   — null clears to portfolio scope
//   onClose:        () => void
//
// Internal states: idle (seed chips + recents) and answer (question + answer + sources + follow-ups).
// Expand button opens a larger <dialog> variant for long threads.
// Styles follow design tokens (T) and the Ask Portfolio mockup.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";

// ─── Seed chips ───────────────────────────────────────────────────────────────

const LOCATION_SEEDS = (label) => [
  `Why is ${label} scored the way it is?`,
  `What signals do we have on ${label}?`,
  `How has utilization changed in the last 90 days at ${label}?`,
  `What lease decisions are coming up at ${label}?`,
  `Compare ${label} to the rest of the portfolio.`,
];

const PORTFOLIO_SEEDS = [
  "What needs attention this week across the portfolio?",
  "Which offices are underutilized?",
  "What are the top lease risks in the next 12 months?",
  "Show me portfolio utilization trends.",
  "Which locations have the highest score?",
];

const LEASE_SEEDS = (label) => [
  `What are the key terms of this lease?`,
  `When does this lease expire?`,
  `What are the cost figures for this lease?`,
  `Are there any critical dates coming up for ${label || "this lease"}?`,
  `What's the renewal status for this lease?`,
];

function getSeedChips(scope) {
  if (!scope || scope.type === "portfolio") return [];
  if (scope.type === "location") return LOCATION_SEEDS(scope.label || "this location");
  if (scope.type === "lease")    return LEASE_SEEDS(scope.label || "this lease");
  return [];
}

// ─── Session storage key for the shortcut hint ───────────────────────────────

const SHORTCUT_HINT_KEY = "ask-shortcut-hint-seen";

// ─── Platform helpers ─────────────────────────────────────────────────────────

function isMac() {
  try {
    const platform =
      navigator.userAgentData?.platform ||
      navigator.platform ||
      "";
    return /mac/i.test(platform);
  } catch {
    return false;
  }
}

function shortcutLabel() {
  return isMac() ? "⌘K" : "Ctrl+K";
}

// ─── Styles (design-token-grounded) ──────────────────────────────────────────

const S = {
  popout: {
    position: "fixed",
    top: 60,
    right: 20,
    width: 460,
    maxHeight: "calc(100vh - 80px)",
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    boxShadow: T.shadowAsk,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 1100,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "13px 16px",
    borderBottom: `1px solid ${T.hairline}`,
    flexShrink: 0,
  },
  scopeChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: T.askScopeBg,
    color: T.askScopeFg,
    fontSize: T.fsXs,
    fontWeight: T.fwBold,
    borderRadius: 7,
    padding: "2px 9px",
    flexShrink: 0,
  },
  inputRow: {
    margin: "13px 16px",
    border: `1px solid ${T.askInputBorder}`,
    borderRadius: 9,
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: "none",
    fontSize: T.fsBase,
    color: T.dark,
    background: "transparent",
    fontFamily: "inherit",
  },
  sendBtn: {
    background: T.accent,
    border: "none",
    borderRadius: 7,
    color: T.white,
    fontSize: T.fsXs,
    fontWeight: T.fwBold,
    padding: "4px 10px",
    cursor: "pointer",
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: T.fsXxxs5,
    fontWeight: T.fwBold,
    textTransform: "uppercase",
    letterSpacing: ".3px",
    color: T.mid,
    margin: "6px 16px 6px",
    flexShrink: 0,
  },
  seedList: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    padding: "0 8px 8px",
  },
  seedItem: (isPortfolio) => ({
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 8px",
    borderRadius: 8,
    fontSize: T.fsSm5,
    cursor: "pointer",
    color: isPortfolio ? T.mid : T.dark,
  }),
  footer: {
    fontSize: T.fsXxs5,
    color: T.light,
    padding: "10px 16px",
    borderTop: `1px solid ${T.hairline}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  questionRow: {
    fontSize: T.fsBase,
    fontWeight: T.fwSemibold,
    padding: "13px 16px",
    borderBottom: `1px solid ${T.hairline}`,
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  answerBody: {
    padding: "14px 16px",
    fontSize: T.fsBase,
    lineHeight: 1.6,
    overflowY: "auto",
    flex: 1,
  },
  answerText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0,
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    color: T.dark,
  },
  basedOn: {
    marginTop: 12,
    background: T.tint,
    border: `1px solid ${T.tintBorder}`,
    borderRadius: 9,
    padding: "10px 12px",
  },
  sourceChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: 7,
    padding: "3px 9px",
    fontSize: T.fsXs,
    color: T.accent,
    fontWeight: T.fwSemibold,
    cursor: "default",
  },
  sourceChipViewData: {
    fontSize: T.fsMin,
    color: T.light,
    borderLeft: `1px solid ${T.border}`,
    paddingLeft: 5,
    marginLeft: 2,
    letterSpacing: ".1px",
  },
  followRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    padding: "0 16px 12px",
    flexShrink: 0,
  },
  followChip: {
    background: T.tint,
    border: `1px solid ${T.tintBorder}`,
    borderRadius: 8,
    padding: "6px 11px",
    fontSize: T.fsXs5,
    cursor: "pointer",
    color: T.dark,
  },
  actRow: {
    display: "flex",
    gap: 8,
    padding: "0 16px 14px",
    flexShrink: 0,
  },
  hintBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    background: T.tint,
    borderBottom: `1px solid ${T.tintBorder}`,
    padding: "7px 14px",
    flexShrink: 0,
  },
  hintText: {
    fontSize: T.fsXs,
    color: T.mid,
    lineHeight: 1.4,
  },
  hintDismiss: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: T.light,
    fontSize: T.fsSm,
    padding: 0,
    lineHeight: 1,
    flexShrink: 0,
  },
};

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div
      style={{
        width: 20, height: 20,
        border: `3px solid ${T.border}`,
        borderTop: `3px solid ${T.accent}`,
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        margin: "24px auto",
      }}
      aria-label="Loading"
      data-testid="ask-popout-spinner"
    />
  );
}

// ─── Answer prose renderer ────────────────────────────────────────────────────
// Strips any residual markdown code-fences (safety net) and renders plain text
// as paragraphs so the answer never surfaces raw JSON braces in the UI.

function renderAnswerProse(text) {
  let clean = String(text ?? "")
    .replace(/^```(?:json)?\s*/im, "")
    .replace(/\s*```\s*$/m, "")
    .trim();

  // If stripping fences left behind a raw JSON object that has an "answer" key,
  // extract just that field so braces never appear in the UI.
  try {
    const parsed = JSON.parse(clean);
    if (parsed && typeof parsed.answer === "string") {
      clean = parsed.answer;
    }
  } catch {
    // not JSON — use the stripped text as-is
  }

  const paragraphs = clean.split(/\n{2,}/);
  return paragraphs.map((para, i) => (
    <p key={i} style={{ margin: i === paragraphs.length - 1 ? 0 : "0 0 8px 0" }}>
      {para.split("\n").map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
}

// ─── Answer state ─────────────────────────────────────────────────────────────

function AnswerPane({ question, answer, sources, followUpChips, model, onFollowUp, onReset, onExpand }) {
  return (
    <>
      <div style={S.questionRow}>
        <span style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", flexShrink: 0 }}>
          You
        </span>
        <span data-testid="ask-question-echo">{question}</span>
      </div>
      <div style={S.answerBody}>
        <div
          data-testid="ask-answer-text"
          style={S.answerText}
        >
          {renderAnswerProse(answer)}
        </div>
        {sources.length > 0 && (
          <div style={S.basedOn}>
            <div style={{
              fontSize: T.fsXxxs5, fontWeight: T.fwBold, textTransform: "uppercase",
              letterSpacing: ".3px", color: T.mid, marginBottom: 6,
            }}>
              Based on
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="ask-sources">
              {sources.map((src, i) => (
                <span key={i} style={S.sourceChip}>
                  {src}
                  <span style={S.sourceChipViewData} aria-label={`View data for ${src}`}>
                    View data ↗
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {followUpChips.length > 0 && (
        <div style={S.followRow} data-testid="ask-followup-chips">
          {followUpChips.map((chip, i) => (
            <button
              key={i}
              type="button"
              style={S.followChip}
              onClick={() => onFollowUp(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
      <div style={S.actRow}>
        <button
          type="button"
          data-testid="ask-expand-button"
          onClick={onExpand}
          style={{
            fontSize: T.fsSm, fontWeight: T.fwSemibold, borderRadius: 8,
            padding: "7px 12px", border: `1px solid ${T.border}`,
            background: T.card, color: T.mid, cursor: "pointer",
          }}
        >
          Expand
        </button>
        <button
          type="button"
          data-testid="ask-new-question-button"
          onClick={onReset}
          style={{
            fontSize: T.fsSm, fontWeight: T.fwSemibold, borderRadius: 8,
            padding: "7px 12px", border: `1px solid ${T.border}`,
            background: T.card, color: T.mid, cursor: "pointer",
          }}
        >
          New question
        </button>
        {model && model !== "n/a" && (
          <span style={{ marginLeft: "auto", fontSize: T.fsXxs5, color: T.light, alignSelf: "center" }}>
            {model}
          </span>
        )}
      </div>
    </>
  );
}

// ─── Expand modal ─────────────────────────────────────────────────────────────

function ExpandModal({ question, answer, sources, followUpChips, onClose }) {
  return (
    <dialog
      open
      data-testid="ask-expand-modal"
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(700px, 90vw)",
        maxHeight: "80vh",
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        boxShadow: T.shadowAskWide,
        padding: 0,
        margin: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 1200,
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px", borderBottom: `1px solid ${T.hairline}`,
      }}>
        <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>✦ Ask Portfolio</span>
        <button
          type="button"
          data-testid="ask-expand-modal-close"
          onClick={onClose}
          style={{ background: "none", border: "none", color: T.mid, cursor: "pointer", fontSize: T.fsLg6 }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        <p style={{ fontWeight: T.fwSemibold, color: T.mid, fontSize: T.fsBase, marginBottom: 12 }}>
          {question}
        </p>
        <pre style={{ ...S.answerText, fontSize: T.fsMd, lineHeight: 1.7 }}>
          {answer}
        </pre>
        {sources.length > 0 && (
          <div style={{ ...S.basedOn, marginTop: 16 }}>
            <div style={{
              fontSize: T.fsXxxs5, fontWeight: T.fwBold, textTransform: "uppercase",
              letterSpacing: ".3px", color: T.mid, marginBottom: 6,
            }}>
              Based on
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sources.map((src, i) => (
                <span key={i} style={S.sourceChip}>
                  {src}
                  <span style={S.sourceChipViewData} aria-label={`View data for ${src}`}>
                    View data ↗
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {followUpChips.length > 0 && (
        <div style={{ ...S.followRow, padding: "12px 20px" }}>
          {followUpChips.map((chip, i) => (
            <span key={i} style={S.followChip}>{chip}</span>
          ))}
        </div>
      )}
    </dialog>
  );
}

// ─── Main AskPopout component ────────────────────────────────────────────────

export function AskPopout({ scope, onScopeChange, onClose, openedViaShortcut, initialQuery = "" }) {
  // A search handoff is a draft, not an automatic request. Send/Enter retains
  // the existing submission path and cannot double-submit on mount.
  const [inputValue,    setInputValue]    = useState(initialQuery);
  const [loading,       setLoading]       = useState(false);
  const [answerState,   setAnswerState]   = useState(null); // null = idle
  const [sessionRecents, setSessionRecents] = useState([]); // session-only
  const [expandOpen,    setExpandOpen]    = useState(false);
  const [showHint,      setShowHint]      = useState(() => {
    if (!openedViaShortcut) return false;
    try {
      if (sessionStorage.getItem(SHORTCUT_HINT_KEY)) return false;
    } catch {
      return false;
    }
    return true;
  });
  const inputRef = useRef(null);

  useEffect(() => {
    if (showHint) {
      try { sessionStorage.setItem(SHORTCUT_HINT_KEY, "1"); } catch { /* ignore */ }
    }
  }, [showHint]);

  const scopeChipLabel = scope && scope.type !== "portfolio"
    ? scope.label || scope.id || scope.type
    : null;

  const locationSeeds = getSeedChips(scope);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Global Escape — dismiss the popout from anywhere (not just when the
  // input is focused). The listener is naturally scoped to the popout's
  // lifetime because AskPopout is only mounted while the popout is open.
  useEffect(() => {
    function handleGlobalEscape(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleGlobalEscape);
    return () => document.removeEventListener("keydown", handleGlobalEscape);
  }, [onClose]);

  const submit = useCallback(async (query, { preserveInput = false } = {}) => {
    if (!query.trim() || loading) return;

    const q = query.trim();
    if (!preserveInput) setInputValue("");
    setLoading(true);
    setAnswerState(null);

    try {
      const body = {
        scope_type: scope?.type ?? "portfolio",
        scope_id:   scope?.id   ?? undefined,
        query: q,
      };

      const resp = await fetch("/api/ask", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Request failed" }));
        setAnswerState({
          question:     q,
          answer:       err.error || "Something went wrong. Please try again.",
          sources:      [],
          followUpChips: [],
          model:        "n/a",
        });
        return;
      }

      const data = await resp.json();
      setAnswerState({
        question:     q,
        answer:       data.answer,
        sources:      data.sources || [],
        followUpChips: data.followUpChips || [],
        model:        data.model,
      });

      setSessionRecents((prev) => [q, ...prev.filter((r) => r !== q)].slice(0, 5));
    } catch {
      setAnswerState({
        question:     q,
        answer:       "Could not reach the AI service. Please try again.",
        sources:      [],
        followUpChips: [],
        model:        "n/a",
      });
    } finally {
      setLoading(false);
    }
  }, [scope, loading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(inputValue);
    }
    // Escape is handled by the global document listener in this component's
    // useEffect, so no per-input branch is needed here.
  };

  const handleFollowUp = (chip) => {
    setAnswerState(null);
    setInputValue(chip);
    submit(chip, { preserveInput: true });
  };

  const handleReset = () => {
    setAnswerState(null);
    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="ask-popout-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          zIndex: 1099,
          background: "transparent",
        }}
      />

      <div
        data-testid="ask-popout"
        style={S.popout}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={S.header}>
          <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ color: T.accent }}>✦</span> Ask
          </span>
          {scopeChipLabel && (
            <span style={S.scopeChip} data-testid="ask-scope-chip">
              {scopeChipLabel}
              <button
                type="button"
                data-testid="ask-scope-chip-clear"
                onClick={() => onScopeChange?.(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: T.askClearFg, padding: 0, lineHeight: 1, fontSize: T.fsXs }}
                aria-label="Clear scope"
              >
                ✕
              </button>
            </span>
          )}
          {!scopeChipLabel && (
            <span
              style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}
              data-testid="ask-portfolio-scope-label"
            >
              Portfolio
            </span>
          )}
          <button
            type="button"
            data-testid="ask-popout-close"
            onClick={onClose}
            aria-label="Close Ask Portfolio"
            style={{
              marginLeft: "auto",
              background: "none", border: "none",
              color: T.light, cursor: "pointer",
              fontSize: T.fsLg, lineHeight: 1,
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Keyboard shortcut hint — shown once per session on first ⌘K open */}
        {showHint && (
          <div style={S.hintBar} data-testid="ask-shortcut-hint">
            <span style={S.hintText}>
              Tip: press {shortcutLabel()} from anywhere to open or close this panel
            </span>
            <button
              type="button"
              data-testid="ask-shortcut-hint-dismiss"
              onClick={() => setShowHint(false)}
              aria-label="Dismiss tip"
              style={S.hintDismiss}
            >
              ✕
            </button>
          </div>
        )}

        {/* Input row */}
        <div style={S.inputRow}>
          <span style={{ color: T.accent, fontSize: T.fsSm }}>✦</span>
          <input
            ref={inputRef}
            data-testid="ask-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              scope?.type === "location"
                ? `Ask about ${scopeChipLabel || "this location"}…`
                : "Ask about the portfolio…"
            }
            style={S.input}
            aria-label="Ask a question"
            disabled={loading}
          />
          <button
            type="button"
            data-testid="ask-submit-button"
            style={{ ...S.sendBtn, opacity: (!inputValue.trim() || loading) ? 0.5 : 1 }}
            onClick={() => submit(inputValue)}
            disabled={!inputValue.trim() || loading}
            aria-label="Submit question"
          >
            Ask
          </button>
        </div>

        {/* Body: loading / idle / answer */}
        {loading && (
          <div style={{ flex: 1 }}>
            <Spinner />
          </div>
        )}

        {!loading && answerState && (
          <AnswerPane
            question={answerState.question}
            answer={answerState.answer}
            sources={answerState.sources}
            followUpChips={answerState.followUpChips}
            model={answerState.model}
            onFollowUp={(chip) => { handleFollowUp(chip); }}
            onReset={handleReset}
            onExpand={() => setExpandOpen(true)}
          />
        )}

        {!loading && !answerState && (
          <>
            {locationSeeds.length > 0 && (
              <>
                <div style={S.sectionLabel} data-testid="ask-location-seeds-label">
                  Suggested for {scopeChipLabel || "this location"}
                </div>
                <div style={S.seedList}>
                  {locationSeeds.map((chip, i) => (
                    <button
                      key={i}
                      type="button"
                      className="wos-btn"
                      data-testid={`ask-seed-${i}`}
                      style={{
                        ...S.seedItem(false),
                        background: "transparent",
                        textAlign: "left",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                      onClick={() => submit(chip)}
                    >
                      <span style={{ color: T.accent, fontSize: T.fsSm }}>✦</span>
                      {chip}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div style={S.sectionLabel}>Across the portfolio</div>
            <div style={S.seedList}>
              {PORTFOLIO_SEEDS.map((chip, i) => (
                <button
                  key={i}
                  type="button"
                  className="wos-btn"
                  data-testid={`ask-portfolio-seed-${i}`}
                  style={{
                    ...S.seedItem(true),
                    background: "transparent",
                    textAlign: "left",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                  onClick={() => submit(chip)}
                >
                  <span style={{ color: T.accent, fontSize: T.fsSm }}>✦</span>
                  {chip}
                </button>
              ))}
            </div>

            {sessionRecents.length > 0 && (
              <>
                <div style={S.sectionLabel}>Recent</div>
                <div style={S.seedList}>
                  {sessionRecents.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      className="wos-btn"
                      data-testid={`ask-recent-${i}`}
                      style={{
                        ...S.seedItem(true),
                        background: "transparent",
                        textAlign: "left",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                      onClick={() => submit(q)}
                    >
                      <span style={{ color: T.mid, fontSize: T.fsSm }}>↩</span>
                      {q}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Footer */}
        <div style={S.footer}>
          <span>Answers cite their sources</span>
          <span>Claude · Gemini</span>
        </div>
      </div>

      {/* Expand modal */}
      {expandOpen && answerState && (
        <>
          <div
            data-testid="ask-expand-modal-backdrop"
            onClick={() => setExpandOpen(false)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 1199,
            }}
          />
          <ExpandModal
            question={answerState.question}
            answer={answerState.answer}
            sources={answerState.sources}
            followUpChips={answerState.followUpChips}
            onClose={() => setExpandOpen(false)}
          />
        </>
      )}
    </>
  );
}
